import type { LucideIcon } from 'lucide-react';
import { HelpCircle } from 'lucide-react';
import { useState } from 'react';

interface MetricCardProps {
    title: string;
    value: string | number;
    hint?: string;
    icon: LucideIcon;
    description?: string;
}

export const MetricCard = ({ title, value, hint, icon: Icon, description }: MetricCardProps) => {
    const [showTooltip, setShowTooltip] = useState(false);

    return (
        <div 
            className="rounded-2xl border border-slate-200/70 dark:border-slate-700/60 bg-white dark:bg-slate-900 p-4 shadow-sm transition-all duration-200 hover:border-slate-300 dark:hover:border-slate-600"
        >
            <div className="flex items-start justify-between gap-3">
                <div className="flex-1">
                    <div className="flex items-center gap-1.5">
                        <span className="text-[11px] uppercase tracking-widest font-bold text-slate-400">
                            {title}
                        </span>
                        {description && (
                            <div className="relative inline-block">
                                <button 
                                    onMouseEnter={() => setShowTooltip(true)}
                                    onMouseLeave={() => setShowTooltip(false)}
                                    className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors focus:outline-none flex items-center"
                                >
                                    <HelpCircle className="w-3.5 h-3.5" />
                                </button>
                                {showTooltip && (
                                    <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 bg-white/95 dark:bg-slate-900/95 border border-slate-200/80 dark:border-slate-800/80 rounded-xl p-3 shadow-xl glass z-30 animate-in fade-in slide-in-from-bottom-1 duration-150 pointer-events-none">
                                        <p className="text-[11px] text-slate-600 dark:text-slate-300 leading-relaxed font-semibold">
                                            {description}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <p className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">{value}</p>
                    {hint ? <p className="mt-1 text-xs text-slate-500">{hint}</p> : null}
                </div>
                <div className="w-9 h-9 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-4 h-4 text-slate-600 dark:text-slate-300" />
                </div>
            </div>
        </div>
    );
};
